// src/pages/Invoices/InvoicePayPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/axios";
import { useUserPermissions } from "@/hooks/useUserPermissions";
import dayjs from "dayjs";
import { Loader2, CheckCircle2, Eye, CreditCard, RefreshCw, X } from "lucide-react";
import { toast } from "react-hot-toast";

/* ------------------------------ Helpers ---------------------------------- */
const currency = (n) => `£${Number(n || 0).toFixed(2)}`;

// Try multiple GET paths until one works
async function tryGet(paths) {
  for (const p of paths) {
    try {
      const res = await api.get(p);
      return res?.data;
    } catch {
      // try next path
    }
  }
  throw new Error("No GET endpoint responded");
}

function paymentErrorMessage(error, fallback) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback
  );
}

/* -------------------------------- Page ----------------------------------- */
export default function InvoicePayPage() {
  const navigate = useNavigate();
  const {
    isAdmin,
    isDeveloper,
    hasPermission,
  } = useUserPermissions();

  const canView =
    Boolean(
      isAdmin ||
      isDeveloper ||
      hasPermission(
        "invoice.invoice.pay"
      )
    );

  const [rows, setRows] = useState([]); // ready-to-pay rows (amount = remaining)
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [detail, setDetail] = useState(null); // selected invoice details (with items, payments, totals)
  const [payAmount, setPayAmount] = useState(""); // partial payment input

  // Redirect if not allowed (route guard should also block)
  useEffect(() => {
    if (!canView) navigate("/forbidden");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  const load = async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const data = await tryGet([
        "/solo-mdsh-api/procurement/invoices/ready-to-pay",
        "/solo-mdsh-api/invoices/ready-to-pay",
      ]);
      setRows(Array.isArray(data) ? data : Array.isArray(data?.rows) ? data.rows : []);
    } catch (e) {
      console.error(e);
      toast.error("Failed to fetch approved invoices");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [canView]);

  const openDetail = async (id) => {
    try {
      const data = await tryGet([
        `/solo-mdsh-api/procurement/invoices/${id}`,
        `/solo-mdsh-api/invoices/${id}`,
      ]);
      setDetail(data || null);
      setPayAmount(""); // reset partial amount on open
    } catch {
      toast.error("Failed to load invoice details");
    }
  };

  const payNow = async (id) => {
    if (processingId) return;
    const label = detail?.invoice_id === id ? "remaining balance" : "invoice";
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Pay ${label} for invoice #${id}?`)) return;

    setProcessingId(id);
    const t = toast.loading("Processing payment…");
    try {
      await api.post(
        `/solo-mdsh-api/procurement/invoices/${id}/pay`
      );

      // remove from list (it should be fully paid now)
      setRows((prev) => prev.filter((r) => r.invoice_id !== id));
      // close modal if it's the same invoice
      if (detail?.invoice_id === id) setDetail(null);
      toast.success(`Invoice #${id} paid`, { id: t });
    } catch (e) {
      console.error(e);
      toast.error(
        paymentErrorMessage(
          e,
          "Payment failed"
        ),
        { id: t }
      );
    } finally {
      setProcessingId(null);
    }
  };

  const payPartial = async (id) => {
    if (processingId) return;
    const amt = Number(payAmount);
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error("Enter a valid amount > 0");
      return;
    }
    const remaining = Number(detail?.totals?.remaining || 0);
    if (amt > remaining) {
      toast.error(`Amount exceeds remaining (${currency(remaining)})`);
      return;
    }

    setProcessingId(id);
    const t = toast.loading("Recording partial payment…");
    try {
      await api.post(
        `/solo-mdsh-api/procurement/invoices/${id}/pay`,
        {
          amount: amt,
          payment_method: "manual",
        }
      );
      // Refresh the list & detail to reflect new remaining and payments
      await load();
      await openDetail(id);
      setPayAmount("");
      toast.success("Partial payment recorded", { id: t });
    } catch (e) {
      console.error(e);
      toast.error(
        paymentErrorMessage(
          e,
          "Partial payment failed"
        ),
        { id: t }
      );
    } finally {
      setProcessingId(null);
    }
  };

  if (!canView) return null;

  // rows[].amount represents REMAINING (from backend)
  const totalRemaining = useMemo(
    () => rows.reduce((s, r) => s + Number(r.amount || 0), 0),
    [rows]
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-950 to-slate-900 text-slate-100">
      <div className="p-4 sm:p-6 space-y-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 rounded-xl bg-white/5 border border-white/10 grid place-items-center shadow">
                <CreditCard className="h-5 w-5 text-slate-100" />
              </div>
              <h1 className="text-2xl font-extrabold tracking-tight">Ready to Pay</h1>
            </div>
            <p className="text-sm text-slate-300 mt-1">
              Total Remaining:{" "}
              <span className="text-slate-50 font-extrabold">{currency(totalRemaining)}</span>
            </p>
          </div>

          <button
            onClick={load}
            disabled={loading}
            className="bg-white/5 hover:bg-white/10 border border-white/10 text-slate-100 px-3 py-2 rounded-lg inline-flex items-center gap-2 disabled:opacity-50 backdrop-blur"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            Refresh
          </button>
        </div>

        {/* Table / Empty */}
        {rows.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6 text-slate-300">
            No approved invoices awaiting payment.
          </div>
        ) : (
          <div className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden">
            <div className="overflow-x-auto">
              {/* ✅ FIX: header + body colors are explicitly set to avoid inherited text-white on light header */}
              <table className="min-w-full table-auto text-sm">
                <thead className="bg-slate-900/70 backdrop-blur border-b border-white/10">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-semibold tracking-wide text-slate-200">
                      Invoice #
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-semibold tracking-wide text-slate-200">
                      Vendor
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-semibold tracking-wide text-slate-200">
                      PO
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-semibold tracking-wide text-slate-200">
                      Remaining
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-semibold tracking-wide text-slate-200">
                      Uploaded
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-semibold tracking-wide text-slate-200">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-white/10">
                  {rows.map((row) => (
                    <tr key={row.invoice_id} className="bg-transparent hover:bg-white/[0.06]">
                      <td className="px-3 py-3 text-slate-100 whitespace-nowrap">
                        {row.invoice_number || `#${row.invoice_id}`}
                      </td>
                      <td className="px-3 py-3 text-slate-100">{row.vendor_name || "—"}</td>
                      <td className="px-3 py-3 text-slate-100">{row.po_id ?? "—"}</td>
                      <td className="px-3 py-3 font-semibold text-slate-50">
                        {currency(row.amount)}
                      </td>
                      <td className="px-3 py-3 text-slate-200">
                        {row.uploaded_at ? dayjs(row.uploaded_at).format("YYYY-MM-DD") : "—"}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => openDetail(row.invoice_id)}
                            className="bg-blue-600/90 hover:bg-blue-600 text-white px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5 border border-white/10"
                          >
                            <Eye className="w-4 h-4" /> View
                          </button>

                          <button
                            onClick={() => payNow(row.invoice_id)}
                            disabled={processingId === row.invoice_id}
                            className="bg-emerald-600/90 hover:bg-emerald-600 text-white px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5 border border-white/10 disabled:opacity-50"
                          >
                            {processingId === row.invoice_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <CheckCircle2 className="w-4 h-4" />
                            )}
                            Pay remaining
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile hint */}
            <div className="px-4 py-3 text-xs text-slate-300 border-t border-white/10">
              Tip: swipe horizontally on mobile to view all columns.
            </div>
          </div>
        )}

        {/* Detail modal */}
        {detail && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3">
            <div className="w-full max-w-5xl rounded-2xl border border-white/10 bg-slate-950/80 text-slate-100 shadow-2xl overflow-hidden backdrop-blur">
              <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-9 w-9 rounded-xl bg-white/5 border border-white/10 grid place-items-center">
                    <CreditCard className="h-5 w-5 text-slate-100" />
                  </div>
                  <div>
                    <div className="text-lg font-semibold">
                      Invoice {detail.invoice_number || detail.invoice_id}
                    </div>
                    <div className="text-xs text-slate-300">
                      Vendor: {detail.vendor_name || "—"} • PO: {detail.po_id ?? "—"}
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => setDetail(null)}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-slate-100"
                >
                  <X className="h-4 w-4" />
                  Close
                </button>
              </div>

              <div className="p-4 sm:p-5 space-y-6 text-sm">
                {/* Header facts */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                    <div className="text-xs text-slate-300">Status</div>
                    <div className="font-semibold">{detail.payment_status || "—"}</div>
                  </div>

                  <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                    <div className="text-xs text-slate-300">Amount Due</div>
                    <div className="font-semibold">
                      {currency(detail.totals?.amount_due ?? detail.amount_due)}
                    </div>
                  </div>

                  <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                    <div className="text-xs text-slate-300">Total Paid</div>
                    <div className="font-semibold">{currency(detail.totals?.total_paid ?? 0)}</div>
                  </div>

                  <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                    <div className="text-xs text-slate-300">Remaining</div>
                    <div className="font-extrabold text-slate-50">
                      {currency(detail.totals?.remaining ?? 0)}
                    </div>
                  </div>

                  <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                    <div className="text-xs text-slate-300">Invoice ID</div>
                    <div className="font-semibold">#{detail.invoice_id}</div>
                  </div>

                  <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                    <div className="text-xs text-slate-300">Invoice No.</div>
                    <div className="font-semibold">{detail.invoice_number || "—"}</div>
                  </div>
                </div>

                {/* Items */}
                <div className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden">
                  <div className="px-4 py-3 border-b border-white/10 font-semibold text-slate-100">
                    Line items
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full table-auto text-xs">
                      <thead className="bg-slate-900/70 border-b border-white/10">
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold text-slate-200">Item</th>
                          <th className="px-3 py-2 text-left font-semibold text-slate-200">
                            Description
                          </th>
                          <th className="px-3 py-2 text-right font-semibold text-slate-200">Qty</th>
                          <th className="px-3 py-2 text-right font-semibold text-slate-200">
                            Unit Price
                          </th>
                          <th className="px-3 py-2 text-right font-semibold text-slate-200">Tax</th>
                          <th className="px-3 py-2 text-right font-semibold text-slate-200">
                            Line Total
                          </th>
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-white/10">
                        {(detail.items || []).map((it, i) => (
                          <tr key={i} className="hover:bg-white/[0.06]">
                            <td className="px-3 py-2 text-slate-100 whitespace-nowrap">
                              {it.item_code || "—"}
                            </td>
                            <td className="px-3 py-2 text-slate-200">
                              {it.description || it.item_name || "—"}
                            </td>
                            <td className="px-3 py-2 text-right text-slate-100">
                              {Number(it.quantity || 0)}
                            </td>
                            <td className="px-3 py-2 text-right text-slate-100">
                              {currency(it.unit_price)}
                            </td>
                            <td className="px-3 py-2 text-right text-slate-100">
                              {currency(it.tax)}
                            </td>
                            <td className="px-3 py-2 text-right font-semibold text-slate-50">
                              {currency(it.line_total)}
                            </td>
                          </tr>
                        ))}
                      </tbody>

                      <tfoot className="border-t border-white/10">
                        <tr>
                          <td colSpan={5} className="px-3 py-2 text-right text-slate-200 font-medium">
                            Subtotal
                          </td>
                          <td className="px-3 py-2 text-right text-slate-100 font-medium">
                            {currency(detail.totals?.subtotal ?? 0)}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={5} className="px-3 py-2 text-right text-slate-200 font-medium">
                            Tax
                          </td>
                          <td className="px-3 py-2 text-right text-slate-100 font-medium">
                            {currency(detail.totals?.tax ?? 0)}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={5} className="px-3 py-2 text-right text-slate-50 font-extrabold">
                            Amount Due
                          </td>
                          <td className="px-3 py-2 text-right text-slate-50 font-extrabold">
                            {currency(detail.totals?.amount_due ?? detail.amount_due)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                {/* Payments history */}
                <div className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden">
                  <div className="px-4 py-3 border-b border-white/10 font-semibold text-slate-100">
                    Payments
                  </div>

                  {(detail.payments || []).length === 0 ? (
                    <div className="p-4 text-xs text-slate-300">No payments recorded yet.</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full table-auto text-xs">
                        <thead className="bg-slate-900/70 border-b border-white/10">
                          <tr>
                            <th className="px-3 py-2 text-left font-semibold text-slate-200">
                              Date
                            </th>
                            <th className="px-3 py-2 text-left font-semibold text-slate-200">
                              Method
                            </th>
                            <th className="px-3 py-2 text-left font-semibold text-slate-200">
                              Source
                            </th>
                            <th className="px-3 py-2 text-right font-semibold text-slate-200">
                              Amount
                            </th>
                            <th className="px-3 py-2 text-left font-semibold text-slate-200">
                              Paid By
                            </th>
                            <th className="px-3 py-2 text-left font-semibold text-slate-200">
                              Notes
                            </th>
                          </tr>
                        </thead>

                        <tbody className="divide-y divide-white/10">
                          {detail.payments.map((p) => (
                            <tr key={p.payment_id || `${p.paid_at}-${p.amount}`} className="hover:bg-white/[0.06]">
                              <td className="px-3 py-2 text-slate-100 whitespace-nowrap">
                                {p.paid_at ? dayjs(p.paid_at).format("YYYY-MM-DD HH:mm") : "—"}
                              </td>
                              <td className="px-3 py-2 text-slate-100">
                                {p.payment_method || "—"}
                              </td>
                              <td className="px-3 py-2 text-slate-200">
                                {p.payment_source || "—"}
                              </td>
                              <td className="px-3 py-2 text-right text-slate-50 font-semibold">
                                {currency(p.amount_paid ?? p.amount)}
                              </td>
                              <td className="px-3 py-2 text-slate-100">{p.paid_by ?? "—"}</td>
                              <td className="px-3 py-2 text-slate-200">{p.notes || "—"}</td>
                            </tr>
                          ))}
                        </tbody>

                        <tfoot className="border-t border-white/10">
                          <tr>
                            <td colSpan={3} className="px-3 py-2 text-right text-slate-200 font-medium">
                              Total Paid
                            </td>
                            <td className="px-3 py-2 text-right text-slate-50 font-semibold">
                              {currency(detail.totals?.total_paid ?? 0)}
                            </td>
                            <td colSpan={2} className="px-3 py-2" />
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </div>

                {/* Actions: Pay remaining + Partial payment */}
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Partial amount"
                      value={payAmount}
                      onChange={(e) => setPayAmount(e.target.value)}
                      className="rounded-lg px-3 py-2 text-sm w-52 bg-white/5 border border-white/10 text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-white/20"
                    />
                    <button
                      onClick={() => payPartial(detail.invoice_id)}
                      disabled={processingId === detail.invoice_id}
                      className="bg-indigo-600/90 hover:bg-indigo-600 text-white px-3 py-2 rounded-lg inline-flex items-center gap-2 border border-white/10 disabled:opacity-50"
                    >
                      {processingId === detail.invoice_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <CheckCircle2 className="w-4 h-4" />
                      )}
                      Record partial
                    </button>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => payNow(detail.invoice_id)}
                      disabled={
                        processingId === detail.invoice_id ||
                        Number(detail?.totals?.remaining ?? 0) <= 0
                      }
                      className="bg-emerald-600/90 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg inline-flex items-center gap-2 border border-white/10 disabled:opacity-50"
                    >
                      {processingId === detail.invoice_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <CheckCircle2 className="w-4 h-4" />
                      )}
                      Pay remaining ({currency(detail.totals?.remaining ?? 0)})
                    </button>

                    {detail.file_url && (
                      <a
                        href={detail.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-slate-100"
                      >
                        Open Original File
                      </a>
                    )}
                  </div>
                </div>

                <div className="text-xs text-slate-400">
                  Notes: This page now forces readable contrast on table headers/body to avoid “white text on light header”
                  issues caused by inherited dark-mode styles.
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
